// OWNER: AREA-EVENTS (event-types/*, messages/*, requests/*). Components go in src/features/events.
// Event type editor: rules (the core), schema, samples, settings, and the simulator.
import { useQuery } from '@tanstack/react-query'
import { createFileRoute, Link } from '@tanstack/react-router'
import { Zap } from 'lucide-react'
import { asApiError } from '@/api/errors'
import { PROPOSAL_ID } from '@/api/queries/proposals'
import { EmptyState } from '@/components/EmptyState'
import { ErrorState } from '@/components/QueryState'
import { Button } from '@/components/ui/button'
import { Skeleton, SkeletonText } from '@/components/ui/skeleton'
import { eventTypeQuery } from '@/features/events/api'
import { EventTypeCrumb } from '@/features/events/crumbs'
import { EventTypeEditor } from '@/features/events/editor/EventTypeEditor'
import { EDITOR_TABS, type EditorTab } from '@/features/events/editor/tabs'
import { ApplicationCrumb } from '@/shell/crumbs'

interface EditorSearch {
  tab?: EditorTab
  simulate?: boolean
  /** Set when opened from an application's Event types tab, to keep that breadcrumb trail. */
  app?: string
  /** A proposal (`prp_…`) to load into the rules or settings for review. */
  proposal?: string
  /** Open the header's pending proposals popover (list badges link here). */
  proposals?: boolean
}

export const Route = createFileRoute('/event-types/$eventTypeId')({
  staticData: {
    crumb: EventTypeCrumb,
    fullBleed: true,
    crumbTrail: (search) => {
      const app = typeof search.app === 'string' ? search.app : undefined
      if (!app) return undefined
      return [
        { pathname: '/applications', crumb: 'Applications' },
        { pathname: `/applications/${app}`, crumb: ApplicationCrumb, params: { appId: app } },
        { pathname: `/applications/${app}/event-types`, crumb: 'Event types' },
      ]
    },
  },
  validateSearch: (s: Record<string, unknown>): EditorSearch => ({
    tab:
      EDITOR_TABS.includes(s.tab as EditorTab) && s.tab !== 'rules'
        ? (s.tab as EditorTab)
        : undefined,
    simulate: s.simulate === true || s.simulate === 'true' || s.simulate === 1 ? true : undefined,
    app: typeof s.app === 'string' && /^app_\w+$/.test(s.app) ? s.app : undefined,
    proposal:
      typeof s.proposal === 'string' && PROPOSAL_ID.test(s.proposal) ? s.proposal : undefined,
    proposals:
      s.proposals === true || s.proposals === 'true' || s.proposals === 1 ? true : undefined,
  }),
  loader: ({ context, params }) => {
    void context.queryClient.prefetchQuery(eventTypeQuery(params.eventTypeId))
  },
  component: EventTypeEditorPage,
})

function EventTypeEditorPage() {
  const { eventTypeId } = Route.useParams()
  const search = Route.useSearch()
  const navigate = Route.useNavigate()
  const q = useQuery(eventTypeQuery(eventTypeId))

  if (q.isPending) {
    return (
      <div className="px-5 pt-6 lg:px-8" aria-busy>
        <Skeleton className="h-7 w-64" />
        <Skeleton className="mt-2 h-4 w-48" />
        <Skeleton className="mt-6 h-9 w-80" />
        <div className="mt-6 space-y-3">
          <Skeleton className="h-28 w-full" />
          <Skeleton className="h-28 w-full" />
        </div>
        <SkeletonText className="mt-6 max-w-xl" />
      </div>
    )
  }
  if (q.error) {
    return (
      <div className="px-5 pt-6 lg:px-8">
        {asApiError(q.error).isNotFound ? (
          <EmptyState
            icon={Zap}
            title="This event type doesn’t exist"
            description="It may have been deleted, or the link is out of date."
            action={
              <Button asChild>
                <Link to="/event-types">Back to event types</Link>
              </Button>
            }
          />
        ) : (
          <ErrorState error={q.error} onRetry={() => void q.refetch()} />
        )}
      </div>
    )
  }

  return (
    <EventTypeEditor
      key={q.data.id}
      eventType={q.data}
      tab={search.tab ?? 'rules'}
      onTabChange={(tab) =>
        void navigate({
          search: (prev) => ({ ...prev, tab: tab === 'rules' ? undefined : tab }),
          replace: true,
        })
      }
      proposalId={search.proposal}
      onProposalClose={() =>
        void navigate({
          search: (prev) => ({ ...prev, proposal: undefined }),
          replace: true,
        })
      }
      proposalsOpen={!!search.proposals}
      onProposalsOpenChange={(open) =>
        void navigate({
          search: (prev) => ({ ...prev, proposals: open || undefined }),
          replace: true,
        })
      }
      simulateOpen={!!search.simulate}
      onSimulateOpenChange={(open) =>
        void navigate({
          search: (prev) => ({ ...prev, simulate: open || undefined }),
          replace: true,
        })
      }
    />
  )
}
